import { Loader, Switch } from '@mantine/core';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InstanceResponseModel } from '../../../../../apis/v1/instances/model';
import { Button, InputField } from '../../../../../components/Atoms';
import DialogBox from '../../../../../components/Atoms/DialogBox/DialogBox';
import { useUpdateInstanceMutate } from '../../../../../hooks/CloudComputeHooks/useUpdateInstanceMutate';

interface GeneralSettingsCardProps {
    instance: InstanceResponseModel;
}

export default function GeneralSettingsCard({ instance }: GeneralSettingsCardProps) {
    const { t } = useTranslation('cloudComputeInstance');
    const updateMutation = useUpdateInstanceMutate();
    const [hostname, setHostname] = useState(instance.hostname);
    const hostnameId = useId();
    const protectionLabelId = useId();
    const protectionDescriptionId = useId();

    useEffect(() => {
        setHostname(instance.hostname);
    }, [instance.hostname]);

    const isSaving = updateMutation.isPending;
    const hostnameChanged = hostname.trim() !== '' && hostname.trim() !== instance.hostname;
    const saveBlocked = !hostnameChanged && !isSaving;

    const saveHostname = () => {
        if (!hostnameChanged || isSaving) return;
        updateMutation.mutate({ id: instance.id, data: { hostname: hostname.trim() } });
    };

    const toggleProtection = (next: boolean) => {
        if (isSaving) return;
        updateMutation.mutate({ id: instance.id, data: { termination_protection: next } });
    };

    return (
        <DialogBox label={t('general.title')} classNames="p-4 gap-6">
            <div className="w-full flex flex-col gap-2">
                <label htmlFor={hostnameId} className="text-sm font-medium text-fg">
                    {t('general.hostname')}
                </label>
                <div className="w-full flex flex-row items-start gap-3">
                    <div className="flex-1">
                        <InputField
                            id={hostnameId}
                            value={hostname}
                            onChange={(e) => setHostname(e.target.value)}
                            readOnly={isSaving}
                            descriptionBottom={t('general.hostnameDescription')}
                        />
                    </div>
                    <Button
                        onClick={saveHostname}
                        aria-disabled={saveBlocked || undefined}
                        aria-busy={isSaving || undefined}
                        style={saveBlocked ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                        size="lg"
                    >
                        {isSaving ? (
                            <div className="flex items-center gap-2">
                                <Loader size={14} color="white" />
                                <span>{t('general.saving')}</span>
                            </div>
                        ) : (
                            t('general.save')
                        )}
                    </Button>
                </div>
            </div>

            <div className="w-full flex flex-row items-center justify-between gap-4">
                <div className="flex flex-col">
                    <span id={protectionLabelId} className="text-base text-fg">
                        {t('general.terminationProtection')}
                    </span>
                    <span id={protectionDescriptionId} className="text-sm text-fg/68">
                        {t('general.terminationProtectionDescription')}
                    </span>
                </div>
                <Switch
                    aria-labelledby={protectionLabelId}
                    aria-describedby={protectionDescriptionId}
                    checked={instance.terminationProtection}
                    onChange={(e) => toggleProtection(e.currentTarget.checked)}
                    size="md"
                    color="green"
                />
            </div>
        </DialogBox>
    );
}
